import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const _IS_SHOWCASE = typeof window !== 'undefined' && window.location.pathname.startsWith('/showcase/');

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!_IS_SHOWCASE);
  const profileRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const pendingFetchRef = useRef(null);
  const [adminVerified, setAdminVerifiedState] = useState(
    () => sessionStorage.getItem('qg_admin_verified') === '1'
  );

  const setAdminVerified = (val) => {
    if (val) {
      sessionStorage.setItem('qg_admin_verified', '1');
    } else {
      sessionStorage.removeItem('qg_admin_verified');
    }
    setAdminVerifiedState(val);
  };

  // ── Server-side profile fetch ──────────────────────────────────────────────
  // Calls /api/auth/profile using the JWT directly — completely bypasses the
  // Supabase IndexedDB auth lock that causes "Lock broken by steal" aborts.
  const fetchProfileViaServer = async (accessToken) => {
    if (!accessToken) return null;
    try {
      const res = await fetch('/api/auth/profile', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return null;
      const { profile } = await res.json();
      return profile || null;
    } catch {
      return null;
    }
  };

  // ── Supabase client fallback (kept for cases where token isn't yet available) ─
  const fetchProfileViaClient = async (userId) => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timed out')), 8000)
      );
      const fetchPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]);
      if (error) {
        console.warn('Profile fetch error:', error?.message || error);
        return null;
      }
      return data || null;
    } catch (err) {
      console.warn('Error fetching profile:', err?.message || err);
      return null;
    }
  };

  // ── Main fetchProfile — prefers server endpoint, falls back to client ────────
  const fetchProfile = async (userId, accessToken) => {
    if (fetchInFlightRef.current && pendingFetchRef.current) {
      return pendingFetchRef.current;
    }
    fetchInFlightRef.current = true;
    const promise = (async () => {
      try {
        // Prefer the lock-free server route
        if (accessToken) {
          const p = await fetchProfileViaServer(accessToken);
          if (p) return p;
        }
        // Fall back to Supabase client
        return await fetchProfileViaClient(userId);
      } finally {
        fetchInFlightRef.current = false;
        pendingFetchRef.current = null;
      }
    })();
    pendingFetchRef.current = promise;
    return promise;
  };

  const applyProfile = (p) => {
    profileRef.current = p;
    setProfile(p);
  };

  useEffect(() => {
    if (_IS_SHOWCASE) return;

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id, session.access_token)
          .then((p) => {
            if (p) {
              applyProfile(p);
            } else {
              applyProfile({
                id: session.user.id,
                email: session.user.email,
                role: 'client',
                full_name: session.user.email?.split('@')[0] || 'User',
                _fallback: true,
              });
            }
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(() => setLoading(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null);

        if (!session?.user) {
          applyProfile(null);
          setLoading(false);
          return;
        }

        const current = profileRef.current;
        if (current && current.id === session.user.id && !current._fallback) {
          setLoading(false);
          return;
        }

        setLoading(true);

        try {
          const p = await fetchProfile(session.user.id, session.access_token);
          if (p) {
            applyProfile(p);
          } else if (!current || current._fallback) {
            applyProfile({
              id: session.user.id,
              email: session.user.email,
              role: 'client',
              full_name: session.user.email?.split('@')[0] || 'User',
              _fallback: true,
            });
          }
        } catch (err) {
          console.error('Auth state change error:', err);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, fullName, role, referralCode, verifiedPhone) => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (authError) throw authError;

    if (authData.user) {
      let referredBy = null;
      let assignedSalesRep = null;

      if (referralCode) {
        const { data: referrer } = await supabase
          .from('profiles')
          .select('id')
          .eq('referral_code', referralCode)
          .maybeSingle();

        if (referrer) {
          referredBy = referrer.id;
          if (role === 'client') {
            assignedSalesRep = referrer.id;
          }
        }
      }

      const profilePayload = {
        id: authData.user.id,
        email,
        full_name: fullName,
        role,
        referred_by: referredBy,
        assigned_sales_rep: assignedSalesRep,
      };
      if (verifiedPhone) profilePayload.phone = verifiedPhone;

      const { error: profileError } = await supabase.from('profiles').insert(profilePayload);
      if (profileError) throw profileError;

      const p = await fetchProfile(authData.user.id, authData.session?.access_token);
      if (p) {
        applyProfile(p);
      } else {
        applyProfile({
          id: authData.user.id,
          email,
          role,
          full_name: fullName,
          _fallback: true,
        });
      }

      if (role === 'sales_rep' || role === 'client') {
        fetch('/api/auth/welcome-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, name: fullName, role, plan: 'free' }),
        }).catch(() => {});
      }
    }

    return authData;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    if (data.user) {
      const p = await fetchProfile(data.user.id, data.session?.access_token);
      if (p) {
        applyProfile(p);
      } else {
        applyProfile({
          id: data.user.id,
          email,
          role: 'client',
          full_name: email.split('@')[0],
          _fallback: true,
        });
      }
    }

    return data;
  };

  const signOut = async () => {
    setUser(null);
    applyProfile(null);
    setAdminVerified(false);
    supabase.auth.signOut().catch(() => {});
  };

  const validateInviteToken = async (token) => {
    try {
      const { data, error } = await supabase
        .from('invites')
        .select('*')
        .eq('secure_token', token)
        .eq('status', 'pending')
        .single();

      if (error || !data) return { valid: false, error: 'Invalid or expired invite link' };

      if (new Date(data.expires_at) < new Date()) {
        await supabase
          .from('invites')
          .update({ status: 'expired' })
          .eq('id', data.id);
        return { valid: false, error: 'This invite has expired' };
      }

      return { valid: true, invite: data };
    } catch (err) {
      return { valid: false, error: 'Failed to validate invite' };
    }
  };

  const signUpWithInvite = async (email, password, fullName, secureToken) => {
    const validation = await validateInviteToken(secureToken);
    if (!validation.valid) throw new Error(validation.error);

    const invite = validation.invite;

    if (invite.email.toLowerCase() !== email.toLowerCase()) {
      throw new Error('Email does not match the invite');
    }

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (authError) throw authError;

    if (authData.user) {
      const { error: profileError } = await supabase.from('profiles').insert({
        id: authData.user.id,
        email,
        full_name: fullName,
        role: invite.role,
      });

      if (profileError) throw profileError;

      await supabase
        .from('invites')
        .update({ status: 'accepted' })
        .eq('id', invite.id);

      const p = await fetchProfile(authData.user.id, authData.session?.access_token);
      if (p) {
        applyProfile(p);
      } else {
        applyProfile({
          id: authData.user.id,
          email,
          role: invite.role,
          full_name: fullName,
          _fallback: true,
        });
      }
    }

    return authData;
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signUpWithInvite,
    validateInviteToken,
    signIn,
    signOut,
    adminVerified,
    setAdminVerified,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
